/// The plan constructor's model: a plan as a list of BLOCKS, mirrored from
/// services/passes/src/utils/blocks.ts (specs/passes.md §2a). Pure — no
/// React, no network — so the summary, the validation, the presets and the
/// drag-and-drop reducers are all unit-tested.
///
/// The server decides; this file only says what the panel SHOWS and refuses
/// to send. Every rule checked here is also checked there, with the same
/// meaning: a block plan bounded by nothing (P2), a night window that does
/// not say it is one (P3), two blocks of a type in one place.

// ---- the wire model (as the server sends and takes it) ----

export type TimeWindow = Readonly<{ days: readonly number[]; from: string; to: string; overnight: boolean }>

export type ZonesBlock = Readonly<{ type: 'zones'; zones: readonly string[] }>
export type HoursBlock = Readonly<{ type: 'hours'; windows: readonly TimeWindow[] }>
export type VisitsBlock = Readonly<{ type: 'visits'; count: number }>
export type LimitsBlock = Readonly<{
  type: 'limits'
  perDay: number | null
  perWeek: number | null
  perMonth: number | null
}>
export type TrainerBlock = Readonly<{ type: 'trainer' }>
export type RuleBlock = ZonesBlock | HoursBlock | VisitsBlock | LimitsBlock | TrainerBlock

export type StartRule = 'on_purchase' | 'on_first_visit' | 'fixed_date' | 'after_previous'
export const START_RULES: readonly StartRule[] = ['on_purchase', 'on_first_visit', 'fixed_date', 'after_previous']

/// `days` OR `months` (calendar months), never both. `months` is optional: a
/// plan saved before it existed carries none and means days.
export type ValidityBlock = Readonly<{
  type: 'validity'
  days: number | null
  months?: number | null
  notAfter: string | null
}>
export type StartBlock = Readonly<{ type: 'start'; rule: StartRule; date: string | null }>
export type StayBlock = Readonly<{ type: 'stay'; minutes: number }>
export type GuestsBlock = Readonly<{ type: 'guests'; count: number }>
export type FreezeBlock = Readonly<{
  type: 'freeze'
  maxDays: number
  minDays: number
  maxCount: number
  paid: boolean
}>
export type EligibilityBlock = Readonly<{ type: 'eligibility'; newClientsOnly: boolean }>
/// «Без знижок»: `allowed: false` — the desk sells at the price or above.
export type DiscountsBlock = Readonly<{ type: 'discounts'; allowed: boolean }>
/// Whole years on the sale day, both ends inclusive; the server checks.
export type AgeBlock = Readonly<{ type: 'age'; minAge: number | null; maxAge: number | null }>
/// «Входить у вартість» — words only.
export type IncludedBlock = Readonly<{ type: 'included'; items: readonly string[] }>
/// «Потрібен чинний абонемент» — the door asks for another active pass.
export type RequiresPassBlock = Readonly<{ type: 'requiresPass' }>
/// «Заняття — окреме відвідування» — a group class the client is marked at
/// spends one more visit from the pack. Needs a pack (the server checks too).
export type ClassVisitBlock = Readonly<{ type: 'classVisit' }>
export type PlanLevelBlock =
  | ValidityBlock
  | StartBlock
  | StayBlock
  | GuestsBlock
  | FreezeBlock
  | EligibilityBlock
  | DiscountsBlock
  | AgeBlock
  | IncludedBlock
  | RequiresPassBlock
  | ClassVisitBlock

export type GroupBlock = Readonly<{ type: 'group'; id: string; label: string; blocks: readonly RuleBlock[] }>
export type PlanBlock = RuleBlock | PlanLevelBlock | GroupBlock
export type PlanRules = Readonly<{ version: 1; blocks: readonly PlanBlock[] }>

export const RULE_TYPES = ['zones', 'hours', 'visits', 'limits', 'trainer'] as const
export const PLAN_TYPES = [
  'validity',
  'start',
  'freeze',
  'stay',
  'guests',
  'included',
  'eligibility',
  'age',
  'discounts',
  'requiresPass',
  'classVisit',
] as const
export type RuleType = (typeof RULE_TYPES)[number]
export type PlanLevelType = (typeof PLAN_TYPES)[number]
export type BlockType = RuleType | PlanLevelType | 'group'

export const MAX_GROUPS = 10

export const isRuleType = (type: string): type is RuleType => (RULE_TYPES as readonly string[]).includes(type)
export const isPlanLevelType = (type: string): type is PlanLevelType =>
  (PLAN_TYPES as readonly string[]).includes(type)

// ---- the draft (what the constructor edits) ----

/// Every block on the canvas carries a `key` — the identity drag-and-drop
/// needs. Keys never reach the server; a group's `id` (its counter's name)
/// does.
type Keyed<T> = T & Readonly<{ key: string }>
export type DraftRule = Keyed<RuleBlock>
export type DraftGroup = Keyed<Readonly<{ type: 'group'; id: string; label: string; blocks: readonly DraftRule[] }>>
export type DraftBlock = Keyed<PlanLevelBlock> | DraftRule | DraftGroup

export type PlanDraft = Readonly<{
  name: string
  description: string
  /// Hryvnias as typed: «1 200», «1200,50». Kopecks on the wire (P4).
  price: string
  branchId: string | null
  blocks: readonly DraftBlock[]
  /// «Продається в застосунку» (specs/online-sale.md §2). Absent = not
  /// touched: the save leaves the plan's flag as it is (O2).
  soldOnline?: boolean
}>

let sequence = 0
/// A fresh canvas key. Not a uuid: it never leaves the browser.
export const newKey = (prefix = 'b'): string => {
  sequence += 1
  return `${prefix}${sequence}-${Math.random().toString(36).slice(2, 7)}`
}

/// A group id: short, stable, unique in the plan (it names the group's
/// counter on every pass sold from it).
export const newGroupId = (taken: readonly string[]): string => {
  const next = Array.from({ length: MAX_GROUPS + taken.length + 1 }, (_, i) => `g${i + 1}`).find(
    (id) => !taken.includes(id),
  )
  return next ?? newKey('g')
}

const withKey = <T extends object>(block: T): Keyed<T> => ({ ...block, key: newKey() })

export const groupsOfDraft = (blocks: readonly DraftBlock[]): readonly DraftGroup[] =>
  blocks.filter((block): block is DraftGroup => block.type === 'group')

export const ruleBlockDefault = (type: RuleType): RuleBlock => {
  switch (type) {
    case 'zones':
      return { type: 'zones', zones: [] }
    case 'hours':
      return { type: 'hours', windows: [{ days: [1, 2, 3, 4, 5], from: '07:00', to: '16:00', overnight: false }] }
    case 'visits':
      return { type: 'visits', count: 12 }
    case 'limits':
      return { type: 'limits', perDay: 1, perWeek: null, perMonth: null }
    case 'trainer':
      return { type: 'trainer' }
  }
}

export const planBlockDefault = (type: PlanLevelType): PlanLevelBlock => {
  switch (type) {
    case 'validity':
      return { type: 'validity', days: 30, notAfter: null }
    case 'start':
      return { type: 'start', rule: 'on_first_visit', date: null }
    case 'freeze':
      return { type: 'freeze', maxDays: 30, minDays: 7, maxCount: 2, paid: false }
    case 'stay':
      return { type: 'stay', minutes: 90 }
    case 'guests':
      return { type: 'guests', count: 1 }
    case 'eligibility':
      return { type: 'eligibility', newClientsOnly: true }
    case 'discounts':
      return { type: 'discounts', allowed: false }
    case 'age':
      // «Дитячий — до 14 років»: the case a club reaches for first.
      return { type: 'age', minAge: null, maxAge: 14 }
    case 'included':
      return { type: 'included', items: [] }
    case 'requiresPass':
      return { type: 'requiresPass' }
    case 'classVisit':
      return { type: 'classVisit' }
  }
}

/// A new block of `type`, keyed. A group gets the next free id and a label
/// the club will rename.
export const makeBlock = (type: BlockType, existing: readonly DraftBlock[], label = ''): DraftBlock => {
  if (type === 'group') {
    const groups = groupsOfDraft(existing)
    return {
      type: 'group',
      key: newKey('g'),
      id: newGroupId(groups.map((g) => g.id)),
      label,
      blocks: [],
    }
  }
  return withKey(isRuleType(type) ? ruleBlockDefault(type) : planBlockDefault(type)) as DraftBlock
}

// ---- where things are, and where they may go ----

/// The container a key lives in: `null` = the top level, a group's key, or
/// `undefined` when there is no such block.
export const containerOf = (blocks: readonly DraftBlock[], key: string): string | null | undefined => {
  if (blocks.some((block) => block.key === key)) return null
  return groupsOfDraft(blocks).find((group) => group.blocks.some((block) => block.key === key))?.key
}

export const findBlock = (blocks: readonly DraftBlock[], key: string): DraftBlock | undefined =>
  blocks.find((block) => block.key === key) ??
  groupsOfDraft(blocks)
    .flatMap((group) => group.blocks)
    .find((block) => block.key === key)

const childrenOf = (blocks: readonly DraftBlock[], container: string | null): readonly DraftBlock[] =>
  container === null ? blocks : (groupsOfDraft(blocks).find((g) => g.key === container)?.blocks ?? [])

export type PlacementRefusal = 'planOnly' | 'noNestedGroups' | 'duplicate' | 'tooManyGroups' | 'noSuchGroup'

/// Whether a block of `type` may go into `container` — the same rules the
/// server's schema enforces (one of a type per container, plan blocks and
/// groups only at the top). `moving` = the block being moved, which does not
/// count against itself.
export const placementRefusal = (
  blocks: readonly DraftBlock[],
  type: BlockType,
  container: string | null,
  moving?: string,
): PlacementRefusal | null => {
  if (container !== null) {
    if (!groupsOfDraft(blocks).some((g) => g.key === container)) return 'noSuchGroup'
    if (type === 'group') return 'noNestedGroups'
    if (!isRuleType(type)) return 'planOnly'
  }
  if (type === 'group') {
    const others = groupsOfDraft(blocks).filter((g) => g.key !== moving)
    return others.length >= MAX_GROUPS ? 'tooManyGroups' : null
  }
  const siblings = childrenOf(blocks, container).filter((block) => block.key !== moving)
  return siblings.some((block) => block.type === type) ? 'duplicate' : null
}

// ---- reducers ----

const mapGroup = (
  blocks: readonly DraftBlock[],
  groupKey: string,
  map: (children: readonly DraftRule[]) => readonly DraftRule[],
): readonly DraftBlock[] =>
  blocks.map((block) =>
    block.type === 'group' && block.key === groupKey ? { ...block, blocks: map(block.blocks) } : block,
  )

const insertAt = <T>(list: readonly T[], index: number, item: T): readonly T[] => {
  const at = Math.max(0, Math.min(index, list.length))
  return [...list.slice(0, at), item, ...list.slice(at)]
}

/// Put an existing block object at `index` of `container` (end if omitted).
const place = (
  blocks: readonly DraftBlock[],
  block: DraftBlock,
  container: string | null,
  index?: number,
): readonly DraftBlock[] => {
  if (container === null) return insertAt(blocks, index ?? blocks.length, block)
  return mapGroup(blocks, container, (children) =>
    insertAt(children, index ?? children.length, block as DraftRule),
  )
}

const without = (blocks: readonly DraftBlock[], key: string): readonly DraftBlock[] =>
  blocks
    .filter((block) => block.key !== key)
    .map((block) =>
      block.type === 'group' ? { ...block, blocks: block.blocks.filter((child) => child.key !== key) } : block,
    )

/// Add a new block of `type`. Refused placements leave the draft as it was
/// (the caller asks `placementRefusal` first to say why).
export const addBlock = (
  draft: PlanDraft,
  type: BlockType,
  container: string | null = null,
  index?: number,
  label?: string,
): { draft: PlanDraft; key: string | null } => {
  if (placementRefusal(draft.blocks, type, container)) return { draft, key: null }
  const block = makeBlock(type, draft.blocks, label)
  return { draft: { ...draft, blocks: place(draft.blocks, block, container, index) }, key: block.key }
}

export const removeBlock = (draft: PlanDraft, key: string): PlanDraft => ({
  ...draft,
  blocks: without(draft.blocks, key),
})

/// Replace a block's fields (its type and key stay).
export const updateBlock = (draft: PlanDraft, key: string, next: DraftBlock): PlanDraft => ({
  ...draft,
  blocks: draft.blocks.map((block) => {
    if (block.key === key) return { ...next, key }
    if (block.type === 'group') {
      return {
        ...block,
        blocks: block.blocks.map((child) => (child.key === key ? ({ ...next, key } as DraftRule) : child)),
      }
    }
    return block
  }),
})

/// Move a block to `index` of `container` — a reorder, or a move into or out
/// of a group. `index` counts the destination WITHOUT the moving block.
/// A refused move leaves the draft as it was.
export const moveBlock = (draft: PlanDraft, key: string, container: string | null, index: number): PlanDraft => {
  const block = findBlock(draft.blocks, key)
  if (!block) return draft
  if (container === key) return draft
  if (placementRefusal(draft.blocks, block.type, container, key)) return draft
  return { ...draft, blocks: place(without(draft.blocks, key), block, container, index) }
}

/// One step up (-1) or down (+1) inside its container — the non-drag way to
/// reorder.
export const nudgeBlock = (draft: PlanDraft, key: string, delta: -1 | 1): PlanDraft => {
  const container = containerOf(draft.blocks, key)
  if (container === undefined) return draft
  const siblings = childrenOf(draft.blocks, container)
  const at = siblings.findIndex((block) => block.key === key)
  const to = at + delta
  if (at < 0 || to < 0 || to >= siblings.length) return draft
  return moveBlock(draft, key, container, to)
}

/// The index a block would have after dropping it over `overKey` in the same
/// container (dnd-kit's arrayMove semantics), for `moveBlock`.
export const indexInContainer = (blocks: readonly DraftBlock[], container: string | null, key: string): number =>
  childrenOf(blocks, container).findIndex((block) => block.key === key)

// ---- to and from the wire ----

/// What leaves the browser for a plan block: trimmed, and a validity without
/// the field it does not use — an older server must not meet `months: null`
/// next to days, nor a newer one both.
const stripPlan = (block: Keyed<PlanLevelBlock>): PlanLevelBlock => {
  const { key: _key, ...rest } = block
  if (rest.type === 'included') return { type: 'included', items: rest.items.map((i) => i.trim()).filter(Boolean) }
  if (rest.type === 'validity') {
    return rest.months != null
      ? { type: 'validity', days: null, months: rest.months, notAfter: rest.notAfter }
      : { type: 'validity', days: rest.days, notAfter: rest.notAfter }
  }
  return rest
}

const stripRule = (block: DraftRule): RuleBlock => {
  switch (block.type) {
    case 'zones':
      return { type: 'zones', zones: block.zones.map((z) => z.trim()).filter(Boolean) }
    case 'hours':
      return {
        type: 'hours',
        windows: block.windows.map((w) => ({ days: [...w.days], from: w.from, to: w.to, overnight: w.to < w.from })),
      }
    case 'visits':
      return { type: 'visits', count: block.count }
    case 'limits':
      return { type: 'limits', perDay: block.perDay, perWeek: block.perWeek, perMonth: block.perMonth }
    case 'trainer':
      return { type: 'trainer' }
  }
}

export const toRules = (blocks: readonly DraftBlock[]): PlanRules => ({
  version: 1,
  blocks: blocks.map((block): PlanBlock => {
    if (block.type === 'group') {
      return { type: 'group', id: block.id, label: block.label.trim(), blocks: block.blocks.map(stripRule) }
    }
    if (isRuleType(block.type)) return stripRule(block as DraftRule)
    return stripPlan(block as Keyed<PlanLevelBlock>)
  }),
})

export const fromRules = (rules: PlanRules): readonly DraftBlock[] =>
  rules.blocks.map((block): DraftBlock =>
    block.type === 'group'
      ? { ...block, key: newKey('g'), blocks: block.blocks.map((child) => withKey(child) as DraftRule) }
      : (withKey(block) as DraftBlock),
  )

// ---- money ----

/// «1 200,50» → 120050 kopecks; '' or junk → null.
export const parsePrice = (text: string): number | null => {
  const cleaned = text.replace(/[\s\u00a0\u202f]/g, '').replace(',', '.')
  if (!/^\d+(\.\d{1,2})?$/.test(cleaned)) return null
  return Math.round(Number(cleaned) * 100)
}

export const priceText = (kopecks: number): string => {
  const uah = kopecks / 100
  return Number.isInteger(uah) ? String(uah) : uah.toFixed(2).replace('.', ',')
}

/// «45 ₴», «19,50 ₴», «2 975,50 ₴»: whole hryvnias without decimals, anything
/// else with both digits of kopecks — never «19,5 ₴», which reads like a
/// typo and puts a column of prices out of line.
export const formatMoney = (kopecks: number, currency = 'UAH'): string => {
  const digits = kopecks % 100 === 0 ? 0 : 2
  const amount = new Intl.NumberFormat('uk-UA', { minimumFractionDigits: digits, maximumFractionDigits: 2 }).format(
    kopecks / 100,
  )
  return `${amount} ${currency === 'UAH' ? '₴' : currency}`
}

/// «1 025,00 ₴», «157,50 ₴»: always both digits of kopecks — for a COLUMN of
/// sums, where «1 025 ₴» over «157,50 ₴» puts the hryvnias out of line. A
/// figure on its own keeps `formatMoney`.
export const formatMoneyColumn = (kopecks: number, currency = 'UAH'): string => {
  const amount = new Intl.NumberFormat('uk-UA', { minimumFractionDigits: 2, maximumFractionDigits: 2 }).format(kopecks / 100)
  return `${amount} ${currency === 'UAH' ? '₴' : currency}`
}

// ---- bounds (P2) ----

export const isBounded = (rules: PlanRules): boolean => {
  const validity = rules.blocks.find((b): b is ValidityBlock => b.type === 'validity')
  if (validity && (validity.days != null || validity.months != null || validity.notAfter != null)) return true
  if (rules.blocks.some((b) => b.type === 'visits')) return true
  const groups = rules.blocks.filter((b): b is GroupBlock => b.type === 'group')
  return groups.length > 0 && groups.every((g) => g.blocks.some((b) => b.type === 'visits'))
}

/// A finite pack anywhere — what `classVisit` spends from.
export const hasVisitPack = (rules: PlanRules): boolean =>
  rules.blocks.some((b) => b.type === 'visits') ||
  rules.blocks.some((b) => b.type === 'group' && b.blocks.some((r) => r.type === 'visits'))

export const HHMM = /^([01]\d|2[0-3]):[0-5]\d$/
export const ISO_DATE = /^\d{4}-\d{2}-\d{2}$/

// ---- what a plan asks of a sale and of the door ----

const planBlock = <T extends PlanLevelBlock['type']>(rules: PlanRules, type: T) =>
  rules.blocks.find((block) => block.type === type) as Extract<PlanLevelBlock, { type: T }> | undefined

/// «Без знижок» — the sell panel offers no price below the list.
export const discountsAllowed = (rules: PlanRules): boolean => planBlock(rules, 'discounts')?.allowed !== false

export const ageLimitsOf = (rules: PlanRules): AgeBlock | null => planBlock(rules, 'age') ?? null

/// «Потрібен чинний абонемент» — the tester shows its switch.
export const requiresPass = (rules: PlanRules): boolean => planBlock(rules, 'requiresPass') !== undefined

export const includedOf = (rules: PlanRules): readonly string[] => planBlock(rules, 'included')?.items ?? []
